import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { StreamChat } from 'stream-chat'
import API from 'api'
import { RootState } from 'App'
import { APP_STREAM_KEY } from 'config'
import { GetUserFilesDocument } from '__generated__/api-types-and-hooks'
import { showErrorToast } from 'utils/toastHelpers'

// getUserFiles links stop working at expiresAt. Get a new one a little early so a slow
// click, or a computer clock that is a bit behind, still opens the file.
const EXPIRY_MARGIN_MS = 30 * 1000

export interface IUserFileLink {
  id?: string | null
  userId?: string | null
  tenantId?: string | null
  name?: string | null
  url?: string | null
  expiresAt?: string | null
  streamMessageId?: string | null
}

// Files sent in chat are now copied to our storage as they are sent. Older ones only have
// Stream's link (or none), which must be read again from the chat message.
const onlyInChat = (file: IUserFileLink) =>
  !!file.streamMessageId && (!file.url || /stream-io-cdn\.com|getstream\.io/.test(file.url))

const needsFreshUrl = (file: IUserFileLink) =>
  onlyInChat(file) ||
  !file.url ||
  (!!file.expiresAt && Date.parse(file.expiresAt) - Date.now() < EXPIRY_MARGIN_MS)

const withoutQuery = (url?: string | null) => url?.split('?')[0]

// Files shared in chat are served by Stream, whose links expire; reading the
// message again returns freshly signed ones.
const getChatFileUrl = async (file: IUserFileLink, viewerId?: string, streamToken?: string) => {
  if (!viewerId || !streamToken) return undefined
  const client = new StreamChat(APP_STREAM_KEY, { timeout: 6000 })
  try {
    await client.connectUser({ id: viewerId }, streamToken)
    const { message } = await client.getMessage(file.streamMessageId!)
    const attachments = (message?.attachments || []).filter((a) => a.asset_url || a.image_url)
    const attachment =
      attachments.find(
        (a) => withoutQuery(a.asset_url || a.image_url) === withoutQuery(file.url)
      ) ||
      attachments.find((a) => (a.title || a.fallback) === file.name) ||
      attachments[0]
    return attachment?.asset_url || attachment?.image_url
  } finally {
    client.disconnectUser().catch(() => undefined)
  }
}

const getUploadedFileUrl = async (file: IUserFileLink) => {
  const res: any = await API.fetch(GetUserFilesDocument, {
    userId: file.userId,
    tenantId: file.tenantId,
  })
  let files = JSON.parse(res?.getUserFiles?.data || '[]')
  // The list arrives JSON-encoded twice (AWSJSON holding a JSON string)
  if (typeof files === 'string') files = JSON.parse(files)
  return (Array.isArray(files) ? files : []).find((f) => f.id === file.id)?.url
}

/**
 * Click handler for a file link. Links that expired (or chat files, whose stored
 * link may have expired) get a fresh link before the file opens in a new tab.
 */
export const useOpenUserFile = () => {
  const { t } = useTranslation()
  const viewer = useSelector((state: RootState) => state.user.user)

  return useCallback(
    (event: React.MouseEvent, file: IUserFileLink) => {
      // Callers may listen on a wrapper around the card: only act on the file link itself
      if (!(event.target as HTMLElement).closest('a') || !needsFreshUrl(file)) return
      event.preventDefault()
      // Open the tab now, while the click still allows it, and point it at the file once known
      const tab = window.open('', '_blank')
      if (tab) tab.opener = null
      const freshUrl = onlyInChat(file)
        ? getChatFileUrl(file, viewer?.id, viewer?.streamAccessToken)
        : getUploadedFileUrl(file)
      freshUrl
        .catch(() => undefined)
        .then((url) => {
          if (url && tab) tab.location.href = url
          else if (url) window.open(url, '_blank', 'noopener,noreferrer')
          else {
            tab?.close()
            showErrorToast(t('chat.files.openFailed'))
          }
        })
    },
    [t, viewer?.id, viewer?.streamAccessToken]
  )
}
